"use client";

import { useEffect } from "react";

import { XCHAIN_IMPORTED_WITHDRAWALS_KEY } from "@/constants/local-storage-keys";
import { useAnalytics } from "@/contexts/analytics-context";
import {
  useXchainWithdrawRoutesQuery,
  type XchainWithdrawRoute,
} from "@/contexts/queries/xchain/withdraw-routes";
import {
  useXchainWithdrawalsQuery,
  type XchainWithdrawal,
} from "@/contexts/queries/xchain/withdrawals";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { boundedKeySet } from "@/hooks/storage/bounded-key-set";
import { useAuthState } from "@/hooks/use-auth-state";
import { addressComparisonKey } from "@/utils/address";

import type { TransferProgressData } from "./transfer-progress-card";
import { useTransferProgress } from "./use-transfer-progress";
import { xchainFeeFormatted, xchainWithdrawStepForStage } from "./xchain-stage";

/**
 * Per owner wallet: which withdrawals have already been turned into a card.
 *
 * Keyed on the 1Click deposit address, which is minted per quote and settles exactly one send —
 * so unlike a deposit collector it identifies the withdrawal outright and never has to firm up.
 */
const importedWithdrawals = boundedKeySet(XCHAIN_IMPORTED_WITHDRAWALS_KEY, 200);

type XchainWithdrawTransfer = Extract<TransferProgressData, { type: "xchain-withdraw" }>;

/** Delivered is the end of the road — the funds are on another chain and nothing else can move. */
function isLocallySettled(step: TransferProgressData["currentStep"]): boolean {
  return step === "completed";
}

/** The refund reason if there is one, otherwise the raw upstream status, so a failed card always says something. */
function failureReasonFor(withdrawal: XchainWithdrawal): string | undefined {
  return withdrawal.refundReason || withdrawal.status || undefined;
}

/**
 * The figure the card renders, preferring what actually arrived.
 *
 * `amountOutFormatted` is the destination-side amount net of 1Click's fee, which is the number the
 * receiving wallet will show; `amountInFormatted` stands in for the window before 1Click reports
 * an output.
 */
function withdrawalAmountFormatted(withdrawal: XchainWithdrawal): string | null {
  const candidate = withdrawal.amountOutFormatted || withdrawal.amountInFormatted;
  return candidate && Number.isFinite(Number(candidate)) ? candidate : null;
}

function withdrawalAmount(withdrawal: XchainWithdrawal): number {
  return Number(withdrawalAmountFormatted(withdrawal) ?? 0);
}

function findRoute(
  routes: XchainWithdrawRoute[] | undefined,
  routeId: string,
): XchainWithdrawRoute | undefined {
  return routes?.find((route) => route.id === routeId);
}

/**
 * Mirrors the server's view of cross-chain (NEAR Intents) withdrawals into transfer progress cards.
 *
 * Unlike the deposit tracker, this is usually *not* what creates the card: the send happens in this
 * browser, so `useIntentsWithdraw` adds one the moment the transfer lands. The tracker advances
 * that card as 1Click settles, and re-creates it after a reload — a withdrawal takes minutes and
 * the tab it started in is frequently gone by the time it finishes.
 */
export function useXchainWithdrawTracker(): void {
  const { state, accountAddress, subaccountAddress, ownerWalletAddress } = useAuthState();
  const { primarySubaccountAddress, getAuthHeaders } = useWriteDexSdk();
  const { transfers, addTransfer, updateTransfer } = useTransferProgress();
  const analytics = useAnalytics();

  const enabled = state === "WALLET_CONNECTED" && Boolean(subaccountAddress);

  const { data: routes, isPending: isRoutesPending } = useXchainWithdrawRoutesQuery({ enabled });
  const { data: withdrawals } = useXchainWithdrawalsQuery({
    accountAddress,
    getAuthHeaders,
    subaccountAddress,
    enabled,
  });

  useEffect(() => {
    if (!withdrawals || !accountAddress || !primarySubaccountAddress || !ownerWalletAddress) return;

    // One card per deposit address, which is unique per withdrawal: a quote is minted per send and
    // settles exactly one of them.
    const existingByAddress = new Map(
      transfers
        .filter(
          (transfer): transfer is XchainWithdrawTransfer => transfer.type === "xchain-withdraw",
        )
        .map((transfer) => [addressComparisonKey(transfer.depositAddress), transfer]),
    );
    const ledger = importedWithdrawals(ownerWalletAddress);
    // The ledger persists raw spellings, so existing dismissals are canonicalized on read.
    const importedKeys = new Set([...ledger.read()].map((key) => addressComparisonKey(key)));
    const newlyImported: string[] = [];

    for (const withdrawal of withdrawals) {
      const step = xchainWithdrawStepForStage(withdrawal.stage);
      const depositKey = addressComparisonKey(withdrawal.depositAddress);
      const existing = existingByAddress.get(depositKey);

      if (existing) {
        if (isLocallySettled(existing.currentStep)) continue;

        const update: Partial<XchainWithdrawTransfer> = {};
        if (existing.currentStep !== step) {
          update.currentStep = step;
          // Only on the transition into a terminal stage: this effect re-runs on every 5s poll.
          if (withdrawal.stage === "delivered" || withdrawal.stage === "failed") {
            analytics.portfolio.xchainWithdraw.settled({
              routeId: withdrawal.routeId,
              status: withdrawal.stage === "delivered" ? "success" : "error",
              stage: withdrawal.stage,
              amount: withdrawalAmount(withdrawal),
              ...(withdrawal.stage === "failed" && { errorCode: failureReasonFor(withdrawal) }),
            });
          }
        }

        // Both or neither, as on deposits: the string is the exact figure and the number is what
        // renders, so letting them advance separately would show one and record another.
        const amountFormatted = withdrawalAmountFormatted(withdrawal);
        const amount = withdrawalAmount(withdrawal);
        if (amountFormatted && amount > 0 && amount !== existing.amount) {
          update.amount = amount;
          update.amountFormatted = amountFormatted;
        }

        const failureReason = step === "transfer-failed" ? failureReasonFor(withdrawal) : undefined;
        if (failureReason && existing.failureReason !== failureReason) {
          update.failureReason = failureReason;
        }

        // Backfill for a card re-created after a reload (the fee is fixed at quote time, so this
        // is the same value the create branch below would have seeded, never a later change).
        const feeFormatted = xchainFeeFormatted(withdrawal);
        if (feeFormatted !== undefined && feeFormatted !== existing.feeFormatted) {
          update.feeFormatted = feeFormatted;
        }

        // Writing unconditionally would rewrite localStorage on every 5s poll and re-render every
        // card with it.
        if (Object.keys(update).length > 0) updateTransfer(existing.id, update);
        continue;
      }

      // Dismissing a card has to stick: the server keeps reporting a settled withdrawal forever.
      if (importedKeys.has(depositKey)) continue;

      // The allowlist names the destination chain and the token. Adding the card before it loads
      // would bake a placeholder into localStorage permanently — but a *failed* allowlist request
      // must not hide real withdrawals, so this waits for the first response and no longer.
      if (isRoutesPending) continue;

      const route = findRoute(routes, withdrawal.routeId);
      const amountFormatted = withdrawalAmountFormatted(withdrawal);
      const feeFormatted = xchainFeeFormatted(withdrawal);

      addTransfer({
        type: "xchain-withdraw",
        depositAddress: withdrawal.depositAddress,
        routeId: withdrawal.routeId,
        amount: withdrawalAmount(withdrawal),
        ...(amountFormatted && { amountFormatted }),
        // Fixed by the quote, so a card built from a reload-imported record already has it.
        ...(feeFormatted !== undefined && { feeFormatted }),
        sourceChain: "Aptos",
        // The chain our own transaction settled on. The leg past it is 1Click's.
        destinationChain: "Aptos",
        // Falls back to the route id so a card always names something: the allowlist request can
        // fail after the withdrawals request succeeded, or the row can be retired server-side
        // while a withdrawal made under it is still in flight.
        destinationChainLabel: route?.chain ?? withdrawal.routeId,
        symbol: route?.destinationSymbol ?? "",
        decimals: route?.sourceDecimals ?? 0,
        destinationAddress: withdrawal.destinationAddress,
        accountToWithdrawTo: withdrawal.destinationAddress,
        // Whichever subaccount actually funded the send, as recorded when the quote was taken. It
        // is what the transfer report files the volume under, so the account the user happens to
        // be on now is only a fallback — for withdrawals quoted before the server recorded this.
        subaccountAddress:
          withdrawal.subaccountAddress || subaccountAddress || primarySubaccountAddress,
        currentStep: step,
        ...(withdrawal.originTxHash && { txHash: withdrawal.originTxHash }),
        ...(step === "transfer-failed" && { failureReason: failureReasonFor(withdrawal) }),
        ...(route &&
          route.estimatedSeconds > 0 && {
            estimatedBridgeTimeSeconds: route.estimatedSeconds,
          }),
      });

      newlyImported.push(withdrawal.depositAddress);
    }

    if (newlyImported.length > 0) ledger.add(...newlyImported);
  }, [
    analytics,
    withdrawals,
    routes,
    isRoutesPending,
    transfers,
    accountAddress,
    subaccountAddress,
    primarySubaccountAddress,
    ownerWalletAddress,
    addTransfer,
    updateTransfer,
  ]);
}
